/**
 * Notification Telemetry Event Schema
 *
 * Layered design: callers provide business fields (XxxEventInput),
 * common context is auto-filled by NotificationLogger.
 */

// ============ Enums ============

export enum ClickType {
  Action = 'action',
  Pagination = 'pagination',
  ViewAll = 'view-all',
}

export enum DismissTrigger {
  ItemDismiss = 'item-dismiss',
  DismissAll = 'dismiss-all',
}

export type Surface = 'UnifiedQueue' | 'TopBanner' | 'FloatingBanner' | 'NotificationGrid';

// ============ Common context — cid, aid, uid, timestamp are auto-injected by ContextProcessor ============

// ============ Notification shape expected by the logger ============

type NotificationId = string | number;

interface TelemetryNotificationAction {
  text?: string;
}

/** Minimal notification fields the telemetry logger reads. Both IExternalNotification and INotification satisfy this. */
export interface ITelemetryNotification {
  id?: NotificationId;
  severity?: string;
  title?: string;
  message?: string | object;
  messageType?: string;
  actionList?: TelemetryNotificationAction[];
  notificationTypeId?: NotificationId;
  notificationSource?: string;
  uiDeliveryId?: NotificationId;
  dismissMode?: string;
  target?: string;
}

// ============ Notification metadata (included in every event) ============
export interface NotificationMetadata extends Pick<
  ITelemetryNotification,
  'notificationTypeId' | 'notificationSource' | 'dismissMode' | 'uiDeliveryId' | 'title' | 'messageType'
> {
  /** INotification.id raw value — NOT guaranteed unique across sources */
  notificationId: NotificationId;
  surface: Surface;
  severity: string;
  /** Notification message text (string only; React components are omitted) */
  message?: string;
  /** Number of action buttons */
  actionCount?: number;
  /** Pipe-delimited action button labels, e.g. "Learn more|Dismiss" */
  actions?: string;
}

// ============ Event input types (passed by callers) ============

export interface ImpressionEventInput extends NotificationMetadata {
  eventType: 'impression';
  /** 1-based position in the list */
  position: number;
  totalCount: number;
  renderInstanceId: string;
}

export interface ClickEventInput extends NotificationMetadata {
  eventType: 'click';
  clickType: ClickType;
  clickTarget?: string;
  clickUrl?: string;
  /** Index within actionList (0-based) */
  actionIndex?: number;
  /** 1-based position in the list at the time of click */
  position?: number;
  /** Total notifications visible at the time of click */
  totalCount?: number;
  renderInstanceId: string;
}

export interface DismissEventInput extends NotificationMetadata {
  eventType: 'dismiss';
  dismissTrigger: DismissTrigger;
  totalDismissed?: number;
  /** 1-based position in the list at the time of dismiss */
  position?: number;
  /** Total notifications visible at the time of dismiss */
  totalCount?: number;
  renderInstanceId: string;
}

export type NotificationLogEventInput = ImpressionEventInput | ClickEventInput | DismissEventInput;

export type NotificationLogEvent = NotificationLogEventInput;

// ============ Logger interface (implemented by NotificationLogger) ============

export interface INotificationLogger {
  logImpression(notification: ITelemetryNotification, surface: Surface, position: number, totalCount: number, renderInstanceId: string): void;
  logClick(notification: ITelemetryNotification, surface: Surface, clickType: ClickType, renderInstanceId: string, clickTarget?: string, clickUrl?: string, actionIndex?: number, position?: number, totalCount?: number): void;
  logDismiss(notification: ITelemetryNotification, surface: Surface, trigger: DismissTrigger, renderInstanceId: string, position?: number, totalCount?: number): void;
}
